import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAcademyStore } from '../../store/academyStore';
import { COURSES } from '../../data/curriculum';
import { BadgeCheck, ArrowLeft, CheckCircle2, Star, Award, Crown, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
/**
 * Master Scout Diploma - The ultimate achievement certificate.
 * Unlocks when ALL courses are completed.
 */
export const DiplomaView = () => {
    const navigate = useNavigate();
    const { currentUser, progress } = useAcademyStore();
    const [scale, setScale] = useState(1);
    const [isGenerating, setIsGenerating] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Diploma theme - royal gold
    const theme = { primary: '#b45309', light: '#fefce8', text: '#78350f', accent: '#fbbf24' };

    // Scale and print logic
    useEffect(() => {
        const handleResize = () => {
            if (!containerRef.current) return;
            const padding = 40;
            const availableWidth = window.innerWidth - padding;
            const availableHeight = window.innerHeight - padding;

            const contentWidth = 1100;
            const contentHeight = 920; // Increased from 850 to fit footer properly

            const scaleW = availableWidth / contentWidth;
            const scaleH = availableHeight / contentHeight;

            const newScale = Math.min(Math.min(scaleW, scaleH), 1.2);
            setScale(newScale);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        // Removed auto-print dialog. Wait for user to click download.
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleDownloadPdf = async () => {
        if (!containerRef.current || !currentUser || isGenerating) return;
        
        try {
            setIsGenerating(true);
            
            // Temporarily reset scale to 1 to ensure html2canvas captures at true resolution
            const originalScale = scale;
            setScale(1);
            
            // Wait for React to render the unscaled version
            await new Promise(resolve => setTimeout(resolve, 100));

            const canvas = await html2canvas(containerRef.current, {
                scale: 2, 
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false
            });
            
            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [1100, 920]
            });
            
            pdf.addImage(imgData, 'JPEG', 0, 0, 1100, 920);
            pdf.save(`${currentUser.username.replace(/\s+/g, '_')}_Master_Scout_Diploma.pdf`);
            
            // Restore original UI scale
            setScale(originalScale);
        } catch (err) {
            console.error('Failed to generate PDF', err);
            alert('Failed to generate PDF. Check console for details.');
        } finally {
            setIsGenerating(false);
        }
    };

    if (!currentUser) {
        return <div>Please login to view your diploma.</div>;
    }

    const userProgress = progress[currentUser.id] || { completedModules: [], unlockedCourses: [], xp: 0, badges: [] };

    // Check if ALL courses are completed
    const getCourseCompletion = () => {
        return COURSES.map(course => {
            const moduleIds = course.modules.map(m => m.id);
            const completedCount = moduleIds.filter(id => userProgress.completedModules.includes(id)).length;
            return {
                course,
                completed: completedCount,
                total: moduleIds.length,
                isComplete: completedCount === moduleIds.length && moduleIds.length > 0
            };
        });
    };

    const courseCompletion = getCourseCompletion();
    const allCoursesComplete = courseCompletion.every(c => c.isComplete);
    const totalModulesCompleted = courseCompletion.reduce((sum, c) => sum + c.completed, 0);
    const totalModules = courseCompletion.reduce((sum, c) => sum + c.total, 0);

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-900 via-yellow-800 to-orange-900 flex items-center justify-center overflow-hidden print:overflow-visible print:bg-white print:block">
            {/* Nav Buttons */}
            <div className="fixed top-4 left-4 print:hidden z-50 flex gap-4">
                <button
                    onClick={() => navigate('/profile')}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-lg hover:bg-slate-50 text-slate-600 font-bold transition-transform hover:scale-105"
                >
                    <ArrowLeft size={16} /> Back
                </button>
                <button
                    onClick={handleDownloadPdf}
                    disabled={isGenerating}
                    className={`flex items-center gap-2 px-4 py-2 ${isGenerating ? 'bg-amber-300 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-400 hover:scale-105 transition-transform'} text-white rounded-lg shadow-lg font-bold`}
                >
                    <Download size={16} /> {isGenerating ? 'Generating...' : 'Download PDF'}
                </button>
            </div>

            {/* Scaled Container */}
            <div
                className="transition-transform duration-200 ease-out origin-center print:transform-none print:w-full print:h-full print:m-0"
                style={{ transform: `scale(${scale})` }}
            >
                <div
                    ref={containerRef}
                    className="w-[1100px] h-[920px] bg-white text-center relative shadow-2xl print:shadow-none mx-auto overflow-hidden flex flex-col"
                    style={{
                        border: `28px solid ${theme.primary}`,
                        backgroundImage: `
                            radial-gradient(circle at center, white 40%, ${theme.light} 100%),
                            repeating-linear-gradient(45deg, ${theme.light} 0px, ${theme.light} 2px, transparent 2px, transparent 12px)
                        `
                    }}
                >
                    {/* Inner Border - Double gold */}
                    <div className="absolute inset-2 border-8 border-double pointer-events-none opacity-60 m-2" style={{ borderColor: theme.accent }}></div>

                    {/* Corner Flourishes */}
                    {[0, 90, 180, 270].map((rot, i) => (
                        <div key={i} className="absolute w-20 h-20 pointer-events-none opacity-80"
                            style={{
                                top: i < 2 ? '1.5rem' : 'auto',
                                bottom: i >= 2 ? '1.5rem' : 'auto',
                                left: i === 0 || i === 3 ? '1.5rem' : 'auto',
                                right: i === 1 || i === 2 ? '1.5rem' : 'auto',
                                color: theme.primary,
                                transform: `rotate(${rot}deg)`
                            }}>
                            <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M0,0 L40,0 C60,0 60,20 80,20 M0,0 L0,40 C0,60 20,60 20,80" />
                                <path d="M10,10 L40,10 C50,10 50,20 60,20 M10,10 L10,40 C10,50 20,50 20,60" opacity="0.5" />
                            </svg>
                        </div>
                    ))}

                    {/* Content Area */}
                    <div className="flex-1 flex flex-col items-center p-12 z-10 relative h-full">

                        {/* Header with Stars */}
                        <div className="mt-2 shrink-0">
                            <div className="flex items-center justify-center gap-4 mb-3" style={{ color: theme.accent }}>
                                <Star size={24} className="fill-current" />
                                <Star size={32} className="fill-current" />
                                <Crown size={40} className="text-amber-600" />
                                <Star size={32} className="fill-current" />
                                <Star size={24} className="fill-current" />
                            </div>

                            <div className="flex items-center justify-center gap-3 mb-2 opacity-70" style={{ color: theme.primary }}>
                                <BadgeCheck size={28} />
                                <span className="uppercase tracking-[0.3em] text-xs font-bold">Gorilla Scouts Academy</span>
                                <BadgeCheck size={28} />
                            </div>

                            <h1 className="text-7xl font-serif font-black mb-1 uppercase tracking-wide leading-none drop-shadow-sm" style={{ color: theme.text }}>
                                Master Diploma
                            </h1>
                            <h2 className="text-2xl font-serif italic font-light" style={{ color: theme.primary }}>
                                Highest Achievement in Digital Mastery
                            </h2>
                        </div>

                        {/* Main Text */}
                        <div className="flex flex-col items-center w-full my-6 shrink-0">
                            <p className="text-lg text-slate-500 italic mb-2 font-serif">This prestigious document certifies that</p>

                            <div
                                className="text-6xl font-bold font-comic mb-3 px-12 py-2 border-b-4 inline-block min-w-[500px]"
                                style={{ color: theme.primary, borderColor: theme.accent }}
                            >
                                {currentUser.username}
                            </div>

                            <p className="text-xl text-slate-600 italic mt-3 mb-2 font-serif">
                                has achieved the rank of
                            </p>

                            <div className="relative">
                                <h3
                                    className="text-4xl font-black uppercase tracking-widest px-10 py-4 rounded-2xl border-4 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-lg"
                                    style={{ color: theme.text, borderColor: theme.accent }}
                                >
                                    <span className="flex items-center gap-4">
                                        <Award size={36} style={{ color: theme.accent }} />
                                        MASTER SCOUT
                                        <Award size={36} style={{ color: theme.accent }} />
                                    </span>
                                </h3>
                            </div>

                            <p className="text-sm text-slate-500 mt-4 font-serif">
                                Having successfully completed all {COURSES.length} courses and {totalModules} training modules
                            </p>
                        </div>

                        {/* Courses Grid - Compact */}
                        <div className="w-full max-w-4xl mt-2 mb-8 shrink-0">
                            <div className="text-center text-xs font-bold uppercase tracking-widest mb-3 text-slate-400">
                                Courses Mastered
                            </div>
                            <div className="flex flex-wrap justify-center gap-2">
                                {courseCompletion.map((item, i) => (
                                    <div key={i} className="flex items-center gap-1.5 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                                        <CheckCircle2 size={12} style={{ color: theme.primary }} />
                                        <span className="font-bold text-amber-800 text-[11px]">{item.course.title.replace(/Course \d+: /, '')}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer / Auth */}
                        <div className="w-full flex justify-between items-end mt-auto relative pb-2 pt-16">
                            {/* Date */}
                            <div className="text-center w-56 relative border-t-2 border-slate-300 pt-2">
                                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-48 h-12 flex items-end justify-center pointer-events-none opacity-0"></div>
                                <div className="w-full font-bold text-slate-600 font-mono text-sm">
                                    {new Date().toLocaleDateString()}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Date Certified</div>
                            </div>

                            {/* Centered Seal */}
                            <div className="absolute left-1/2 bottom-2 transform -translate-x-1/2">
                                <div className="w-28 h-28 flex items-center justify-center transform -rotate-6 transition-transform hover:rotate-0">
                                    <img
                                        src="/assets/official_seal.png"
                                        alt="Official Seal"
                                        className="w-full h-full object-contain mix-blend-multiply opacity-90 drop-shadow-xl"
                                    />
                                </div>
                            </div>

                            {/* Signature */}
                            <div className="text-center w-56 relative border-t-2 border-slate-300 pt-2">
                                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-48 h-20 flex items-end justify-center pointer-events-none">
                                    <img
                                        src="/assets/signature_bambam.png"
                                        alt="Bam Bam Signature"
                                        className="h-full w-full object-contain mix-blend-multiply opacity-90 filter contrast-125"
                                    />
                                </div>
                                <div className="w-full font-bold font-comic text-slate-800 relative z-10 text-sm">
                                    Bam Bam
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Scout Leader</div>
                            </div>
                        </div>
                    </div>

                    <style type="text/css" media="print">{`
                        @page { size: landscape; margin: 0; }
                        body { background: white; -webkit-print-color-adjust: exact; padding: 0; margin: 0; }
                    `}</style>
                </div>
            </div>
        </div>
    );
};
